import { useCallback, useEffect, useRef } from "react";

export default function useDragScroll({ loop = false, auto = false, speed = 0.45 } = {}) {
  const scrollerRef = useRef(null);
  const hoveringRef = useRef(false);
  const dragState = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    scrollLeft: 0,
    moved: false,
  });
  const loopState = useRef({
    ready: false,
    segmentWidth: 0,
  });

  const normalizeLoopScroll = useCallback(() => {
    if (!loop) return;
    const scroller = scrollerRef.current;
    const segmentWidth = loopState.current.segmentWidth;
    if (!scroller || !segmentWidth) return;

    if (scroller.scrollLeft < segmentWidth * 0.5) {
      scroller.scrollLeft += segmentWidth;
    } else if (scroller.scrollLeft > segmentWidth * 1.5) {
      scroller.scrollLeft -= segmentWidth;
    }
  }, [loop]);

  useEffect(() => {
    if (!loop) return undefined;

    let frame = 0;
    const setupLoop = () => {
      const scroller = scrollerRef.current;
      if (!scroller) return;

      loopState.current.segmentWidth = scroller.scrollWidth / 3;
      if (!loopState.current.ready && loopState.current.segmentWidth) {
        scroller.scrollLeft = loopState.current.segmentWidth;
        loopState.current.ready = true;
      }
    };

    frame = window.requestAnimationFrame(setupLoop);
    window.addEventListener("resize", setupLoop);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", setupLoop);
    };
  }, [loop]);

  useEffect(() => {
    if (!loop || !auto) return undefined;

    let frame = 0;
    let previous = 0;

    const tick = (time) => {
      const scroller = scrollerRef.current;
      if (scroller && previous && !dragState.current.active && !hoveringRef.current) {
        const delta = Math.min(time - previous, 34);
        scroller.scrollLeft += speed * delta;
        normalizeLoopScroll();
      }
      previous = time;
      frame = window.requestAnimationFrame(tick);
    };

    frame = window.requestAnimationFrame(tick);

    return () => window.cancelAnimationFrame(frame);
  }, [auto, loop, normalizeLoopScroll, speed]);

  const onPointerDown = (event) => {
    const scroller = scrollerRef.current;
    if (!scroller || event.button !== 0) return;

    dragState.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      scrollLeft: scroller.scrollLeft,
      moved: false,
    };
    scroller.setPointerCapture?.(event.pointerId);
    scroller.classList.add("is-dragging");
  };

  const endDrag = (event) => {
    const scroller = scrollerRef.current;
    if (!scroller || dragState.current.pointerId !== event.pointerId) return;

    dragState.current.active = false;
    dragState.current.pointerId = null;
    scroller.releasePointerCapture?.(event.pointerId);
    scroller.classList.remove("is-dragging");
    normalizeLoopScroll();
  };

  const onPointerMove = (event) => {
    const scroller = scrollerRef.current;
    const state = dragState.current;
    if (!scroller || !state.active || state.pointerId !== event.pointerId) return;

    const distance = event.clientX - state.startX;
    if (Math.abs(distance) > 6) {
      state.moved = true;
    }
    scroller.scrollLeft = state.scrollLeft - distance;
    normalizeLoopScroll();
  };

  const onClickCapture = (event) => {
    if (!dragState.current.moved) return;
    event.preventDefault();
    event.stopPropagation();
    dragState.current.moved = false;
  };

  const onScroll = () => {
    normalizeLoopScroll();
  };

  const onMouseEnter = () => {
    hoveringRef.current = true;
  };

  const onMouseLeave = () => {
    hoveringRef.current = false;
  };

  return {
    ref: scrollerRef,
    onPointerDown,
    onPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    onClickCapture,
    onScroll,
    onMouseEnter,
    onMouseLeave,
  };
}
